/* METRO cart - instant quantity control.
 *
 * Loaded AFTER metro.js. It takes over the cart drawer's +, - and remove
 * buttons with a capture-phase listener, so metro.js's own delegate never
 * runs for them, and replaces MetroCart.add / MetroCart.change.
 *
 * WHY THE PREVIOUS VERSION STILL FELT SLOW
 * metro.js resolves the target quantity from the button's `data-cart-change`
 * attribute, which is rendered by Liquid. The old optimistic update rewrote
 * only the <span> holding the number, so the buttons kept advertising the
 * pre-click quantity. A second click therefore re-sent the value that was
 * already in flight - a no-op. Every click had to wait for a full round trip
 * and a section repaint before the next one counted for anything.
 *
 * HOW IT WORKS NOW
 * - The row itself carries the quantity (`data-cart-qty-value`), so the DOM is
 *   the source of truth and a click can read it without touching the server.
 * - A click paints the new number, the button attributes and the badge
 *   immediately, then marks the line dirty.
 * - Dirty lines are collapsed and sent as ONE `/cart/update.js` after a short
 *   idle window. Ten taps across three products cost one request, not ten.
 * - Lines are addressed by `item.key`, never by position. Positional `line`
 *   indices shift as soon as another line is removed, which used to send a
 *   quantity against whichever product had slid into that slot.
 * - Responses are parsed defensively. Shopify answers throttled bursts with
 *   HTML, which made `res.json()` throw and surfaced the generic
 *   "Something went wrong updating your cart" toast even though nothing was
 *   actually wrong. Those get one silent retry.
 */

/* Meta Pixel - AddToCart */
function metroFbqAddToCart(item) {
  if (typeof fbq === 'undefined') return;
  fbq('track', 'AddToCart', {
    content_ids: [String(item.variant_id || item.id || '')],
    content_type: 'product',
    value: (parseFloat(item.price) || 0) / 100,
    currency: 'USD',
    contents: [{
      id: String(item.variant_id || item.id || ''),
      quantity: parseInt(item.quantity) || 1
    }]
  });
}


(function () {
  'use strict';

  var Theme = window.MetroTheme || {};
  var Cart = window.MetroCart;
  if (!Cart) return;

  var routes = Theme.routes || {};
  var strings = Theme.strings || {};
  var SECTION = 'cart-drawer';

  /* Long enough to swallow a burst of taps, short enough to feel immediate.
     The number on screen has already moved, so this is invisible latency. */
  var COALESCE_MS = 180;

  function $(sel, root) {
    return (root || document).querySelector(sel);
  }
  function $$(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }
  function toastError(message) {
    if (window.MetroToast) window.MetroToast.error(message || strings.cartError);
  }

  /* ------------------------------------------------------------ transport */

  function request(url, payload, attempt) {
    payload.sections = SECTION;
    payload.sections_url = window.location.pathname;

    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (res) {
      return res.text().then(function (text) {
        var json = null;
        try {
          json = JSON.parse(text);
        } catch (err) {
          /* not JSON - a throttle or edge error page */
        }
        if (!json && (res.status === 429 || res.status >= 500) && (attempt || 0) < 1) {
          return new Promise(function (resolve) {
            setTimeout(resolve, 400);
          }).then(function () {
            return request(url, payload, (attempt || 0) + 1);
          });
        }
        return { ok: res.ok, status: res.status, json: json };
      });
    });
  }

  /* -------------------------------------------------------------- painting */

  function rows() {
    return $$('[data-cart-line]');
  }

  function quantityOf(row) {
    return Number(row.getAttribute('data-cart-qty-value')) || 0;
  }

  function localCount() {
    return rows().reduce(function (sum, row) {
      return sum + quantityOf(row);
    }, 0);
  }

  function paintCount(count) {
    if (count === null || count === undefined || isNaN(count)) return;
    Theme.cartCount = count;
    $$('[data-cart-count]').forEach(function (el) {
      el.textContent = count;
    });
  }

  /* Move the number, and the buttons that quote it, in the same frame. */
  function paintRow(row, quantity) {
    row.setAttribute('data-cart-qty-value', quantity);

    var out = $('[data-cart-qty]', row);
    if (out) out.textContent = quantity;

    var minus = $('[data-cart-role="minus"]', row);
    var plus = $('[data-cart-role="plus"]', row);
    if (minus) {
      minus.setAttribute('data-cart-change', Math.max(0, quantity - 1));
      minus.disabled = quantity <= 0;
    }
    if (plus) plus.setAttribute('data-cart-change', quantity + 1);

    if (quantity <= 0) row.hidden = true;
  }

  function paintSection(json) {
    var html = json && json.sections && json.sections[SECTION];
    if (!html) return false;

    var doc = new DOMParser().parseFromString(html, 'text/html');

    var fresh = doc.querySelector('[data-cart-drawer-body]');
    var current = $('[data-cart-drawer-body]');
    if (fresh && current) current.innerHTML = fresh.innerHTML;

    /* The summary sentence sits in the drawer header, outside the body. */
    var freshSummary = doc.querySelector('[data-cart-summary]');
    var summary = $('[data-cart-summary]');
    if (freshSummary && summary) summary.textContent = freshSummary.textContent;

    var freshTotal = doc.querySelector('[data-cart-total]');
    if (freshTotal) {
      $$('[data-cart-total]').forEach(function (el) {
        el.textContent = freshTotal.textContent;
      });
    }
    return true;
  }

  /* Pull the server's version of the cart after anything went wrong. */
  function resync() {
    return fetch(routes.root + '?sections=' + SECTION, {
      headers: { Accept: 'application/json' }
    })
      .then(function (res) {
        return res.json();
      })
      .then(function (json) {
        paintSection({ sections: json });
        paintCount(localCount());
      })
      .catch(function () {
        /* keep whatever is on screen */
      });
  }

  /* ----------------------------------------------------------------- queue */

  var dirty = {};
  var timer = null;
  var inflight = false;

  function isDirty() {
    return Object.keys(dirty).length > 0;
  }

  function schedule() {
    if (timer) clearTimeout(timer);
    timer = setTimeout(function () {
      timer = null;
      flush();
    }, COALESCE_MS);
  }

  function flush() {
    if (inflight || !isDirty()) return Promise.resolve();

    var updates = dirty;
    dirty = {};
    inflight = true;

    return request(routes.cartUpdate, { updates: updates })
      .then(function (result) {
        if (!result.ok) {
          toastError(result.json && (result.json.description || result.json.message));
          dirty = {};
          return resync();
        }
        /* A newer click landed while this was in flight. Leave the screen on
           the customer's number; the next response repaints from the server. */
        if (isDirty()) return;

        paintSection(result.json);
        paintCount(
          result.json && result.json.item_count !== undefined
            ? result.json.item_count
            : localCount()
        );
        document.dispatchEvent(
          new CustomEvent('metro:cart:updated', { detail: result.json })
        );
      })
      .catch(function () {
        toastError();
        dirty = {};
        return resync();
      })
      .then(function () {
        inflight = false;
        if (isDirty()) return flush();
      });
  }

  /* ------------------------------------------------------------- handling */

  function apply(row, quantity) {
    var key = row.getAttribute('data-cart-key');
    if (!key) return false;

    quantity = Math.max(0, Number(quantity) || 0);
    paintRow(row, quantity);
    paintCount(localCount());
    dirty[key] = quantity;
    schedule();
    return true;
  }

  /* Capture phase: this runs before metro.js's document-level delegate, and
     stopping propagation there keeps the old stale-attribute path from firing
     a second, contradictory request. */
  document.addEventListener(
    'click',
    function (event) {
      var target =
        event.target.closest &&
        event.target.closest('[data-cart-change], [data-cart-remove]');
      if (!target) return;

      var row = target.closest('[data-cart-line]');
      if (!row || !row.getAttribute('data-cart-key')) return; // let metro.js have it

      event.preventDefault();
      event.stopPropagation();

      if (target.hasAttribute('data-cart-remove')) {
        apply(row, 0);
        return;
      }

      var role = target.getAttribute('data-cart-role');
      var current = quantityOf(row);
      if (role === 'minus') apply(row, current - 1);
      else if (role === 'plus') apply(row, current + 1);
      else apply(row, Number(target.getAttribute('data-cart-change')));
    },
    true
  );

  /* ------------------------------------------------------------ public API */

  Cart.change = function (line, quantity) {
    var row = $('[data-cart-line][data-cart-line-index="' + Number(line) + '"]');
    if (row && apply(row, quantity)) return Promise.resolve();

    /* No matching row - fall back to a direct positional change. */
    return request(routes.cartChange, {
      line: Number(line),
      quantity: Number(quantity)
    })
      .then(function (result) {
        if (!result.ok) {
          toastError(result.json && (result.json.description || result.json.message));
          return;
        }
        paintSection(result.json);
        paintCount(result.json && result.json.item_count);
      })
      .catch(function () {
        toastError();
      });
  };

  Cart.add = function (id, quantity, properties) {
    var qty = Number(quantity) || 1;
    var item = { id: id, quantity: qty };
    if (properties) item.properties = properties;

    /* The badge moves on the tap, not on the response. add.js returns only the
       added line, so this is reconciled from the section markup below. */
    var before = Theme.cartCount || 0;
    paintCount(before + qty);

    return request(routes.cartAdd, { items: [item] })
      .then(function (result) {
        if (!result.ok) {
          paintCount(before);
          toastError(result.json && (result.json.description || result.json.message));
          return null;
        }
        if (paintSection(result.json)) paintCount(localCount());
        document.dispatchEvent(
          new CustomEvent('metro:cart:updated', { detail: result.json })
        );
        return result.json || {};
      })
      .catch(function () {
        paintCount(before);
        toastError();
        return null;
      });
  };

  /* Open the drawer on the tap rather than on the response. metro.js opens it
     again once the add resolves; openDrawer() is a no-op when already open. */
  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!form || !form.hasAttribute || !form.hasAttribute('data-cart-form')) return;
      if (form.getAttribute('data-open-drawer') === 'false') return;
      if (window.MetroDrawer) window.MetroDrawer.open('cart');
    },
    true
  );

  /* metro.js gates its own add path on this flag; keep it permanently open so
     a second tap is never silently dropped. */
  Object.defineProperty(Cart, 'busy', {
    get: function () {
      return false;
    },
    set: function () {},
    configurable: true
  });
})();


/* Track AddToCart via cart/add.js interceptor */
(function() {
  var origFetch = window.fetch;
  window.fetch = function() {
    var url = arguments[0];
    var result = origFetch.apply(this, arguments);
    if (typeof url === 'string' && url.indexOf('/cart/add.js') > -1) {
      result.then(function(res) {
        if (res.ok) {
          return res.clone().json().then(function(data) {
            window.__lastAddedItem = {
              variant_id: data.variant_id || data.id,
              price: data.price,
              quantity: data.quantity || 1
            };
          }).catch(function(){});
        }
      }).catch(function(){});
    }
    return result;
  };
})();

